import {
	FlexDirection,
	LayoutCrossAlignment,
	LayoutGapValue,
	LayoutGridChildSpan,
	LayoutGroupModel,
	LayoutMainAlignment,
	LayoutMode,
	LayoutPadding,
	LayoutStackingOrder,
	LayoutTrackDefinition,
	LayoutWrapMode,
	mergeLayoutGroup
} from '../../../../../../../api/src/creative-unit-package/models/layer/layout-group.dto';
import { LayerDto } from '../../../../../../../api/src/creative-unit-package/models/layer/layer.dto';
import { LayoutStyleMutation } from './layout-style.interfaces';
import { CSSUtils } from '../../../utils/css.utils';

type LayerStyle = Partial<LayerDto['style']>;

export class LayoutStyleUtils {
	static applyLayoutChange(
		existingLayout: LayoutGroupModel,
		change: Partial<LayoutGroupModel>,
		childLayerIds?: string[],
		fixedSizeChildren: string[] = [],
		forceRedraw: boolean = false
	): LayoutStyleMutation {
		const layout = mergeLayoutGroup(existingLayout, change);
		const stylePatch = this.buildStylePatch(layout);
		const childStylePatches = this.buildChildStylePatches(
			layout,
			existingLayout.mode,
			childLayerIds,
			fixedSizeChildren,
			forceRedraw
		);

		const mutation: LayoutStyleMutation = {
			layout
		};

		if (stylePatch && Object.keys(stylePatch).length) {
			mutation.stylePatch = stylePatch;
		}

		if (childStylePatches && Object.keys(childStylePatches).length) {
			mutation.childStylePatches = childStylePatches;
		}

		if (layout.ignoreFlowIds?.length) {
			mutation.ignoreFlowIds = [...layout.ignoreFlowIds];
		}

		return mutation;
	}

	private static buildStylePatch(layout: LayoutGroupModel): LayerStyle {
		const patch: LayerStyle = {};

		this.applyPadding(patch, layout.spacing?.padding);

		switch (layout.mode) {
			case LayoutMode.Freeform:
				this.applyFreeformStyles(patch);
				break;
			case LayoutMode.FlexHorizontal:
				this.applyFlexStyles(patch, layout, 'row');
				break;
			case LayoutMode.FlexVertical:
				this.applyFlexStyles(patch, layout, 'column');
				break;
			case LayoutMode.Grid:
				this.applyGridStyles(patch, layout);
				break;
		}

		return patch;
	}

	private static applyFreeformStyles(patch: LayerStyle): void {
		patch.display = 'block';
		this.setStyleValue(patch, 'flexDirection', undefined);
		this.setStyleValue(patch, 'flexWrap', undefined);
		this.setStyleValue(patch, 'justifyContent', undefined);
		this.setStyleValue(patch, 'alignItems', undefined);
		this.setStyleValue(patch, 'alignContent', undefined);
		this.setStyleValue(patch, 'columnGap', undefined);
		this.setStyleValue(patch, 'rowGap', undefined);
		this.setStyleValue(patch, 'gridTemplateColumns', undefined);
		this.setStyleValue(patch, 'gridTemplateRows', undefined);
	}

	private static applyFlexStyles(
		patch: LayerStyle,
		layout: LayoutGroupModel,
		direction: 'row' | 'column'
	): void {
		patch.display = 'flex';
		patch.flexDirection = direction;

		const wrap = layout.flex?.wrap === LayoutWrapMode.Wrap ? 'wrap' : 'nowrap';
		patch.flexWrap = wrap;

		this.setStyleValue(patch, 'boxSizing', 'border-box');

		this.setStyleValue(
			patch,
			'justifyContent',
			this.mapFlexMainAlignment(layout.alignment?.main)
		);

		this.setStyleValue(
			patch,
			'alignItems',
			this.mapFlexCrossAlignment(layout.alignment?.cross, layout.alignment?.baseline)
		);

		this.setStyleValue(
			patch,
			'alignContent',
			this.mapFlexAlignContent(layout.alignment?.cross, wrap)
		);

		const mainGap = this.toCssGap(layout.spacing?.gap);
		const crossGap = this.toCssGap(layout.spacing?.crossGap);

		if (direction === 'row') {
			this.setStyleValue(patch, 'columnGap', mainGap);
			this.setStyleValue(patch, 'rowGap', crossGap);

			const direction = layout.flex?.direction == FlexDirection.Reverse ? 'row-reverse' : 'row';
			this.setStyleValue(patch, 'flexDirection', direction);
		} else {
			this.setStyleValue(patch, 'rowGap', mainGap);
			this.setStyleValue(patch, 'columnGap', crossGap);

			const direction = layout.flex?.direction == FlexDirection.Reverse ? 'column-reverse' : 'column';
			this.setStyleValue(patch, 'flexDirection', direction);
		}

		this.resetGridStyles(patch);
	}

	private static applyGridStyles(patch: LayerStyle, layout: LayoutGroupModel): void {
		patch.display = 'grid';

		this.setStyleValue(patch, 'boxSizing', 'border-box');

		const columnsTemplate = this.buildTrackTemplate(layout.grid?.columns ?? []);
		const rowsTemplate = this.buildTrackTemplate(layout.grid?.rows ?? []);

		this.setStyleValue(patch, 'gridTemplateColumns', columnsTemplate);
		this.setStyleValue(patch, 'gridTemplateRows', rowsTemplate);

		const columnGap = this.toCssGap(layout.grid?.columnGap ?? layout.spacing?.gap);
		const rowGap = this.toCssGap(layout.grid?.rowGap ?? layout.spacing?.crossGap);

		this.setStyleValue(patch, 'columnGap', columnGap);
		this.setStyleValue(patch, 'rowGap', rowGap);

		// grid alignment of cells
		this.setStyleValue(
			patch,
			'justifyContent',
			this.mapGridMainAlignment(layout.alignment?.main)
		);
		this.setStyleValue(
			patch,
			'alignContent',
			this.mapGridCrossAlignment(layout.alignment?.cross)
		);

		// element alignment inside grid cells
		this.setStyleValue(
			patch,
			'alignItems',
			this.mapGridItemAlignment(layout.alignment?.alignItems, layout.alignment?.baseline)
		);
		this.setStyleValue(
			patch,
			'justifyItems',
			this.mapGridItemAlignment(layout.alignment?.justifyItems, layout.alignment?.baseline)
		);

		this.resetFlexStyles(patch);
	}

	private static applyPadding(patch: LayerStyle, padding?: LayoutPadding): void {
		this.setStyleValue(patch, 'paddingTop', padding?.top);
		this.setStyleValue(patch, 'paddingRight', padding?.right);
		this.setStyleValue(patch, 'paddingBottom', padding?.bottom);
		this.setStyleValue(patch, 'paddingLeft', padding?.left);
	}

	private static resetGridStyles(patch: LayerStyle): void {
		this.setStyleValue(patch, 'gridTemplateColumns', undefined);
		this.setStyleValue(patch, 'gridTemplateRows', undefined);
		this.setStyleValue(patch, 'justifyItems', undefined);
	}

	private static resetFlexStyles(patch: LayerStyle): void {
		this.setStyleValue(patch, 'flexDirection', undefined);
		this.setStyleValue(patch, 'flexWrap', undefined);
	}

private static buildFitContainerStyles(mode: LayoutMode): Partial<LayerStyle> {
		switch (mode) {
			case LayoutMode.FlexHorizontal:
				return { flex: '1', height: '100%' };
			case LayoutMode.FlexVertical:
				return { width: '100%', flex: '1' };
			case LayoutMode.Grid:
				return { width: '100%', height: '100%' };
			default:
				return {};
		}
	}

	private static buildChildStylePatches(
		layout: LayoutGroupModel,
		previousMode?: LayoutMode,
		childLayerIds?: string[],
		fixedSizeChildren: string[] = [],
		forceRedraw: boolean = false
	): Record<string, LayerStyle> | undefined {
		const patches: Record<string, LayerStyle> = {};

		// Reset positioning styles when transitioning FROM freeform TO flow layouts
		const isTransitioningToFlow =
			previousMode === LayoutMode.Freeform && layout.mode !== LayoutMode.Freeform;

		// Also reset when switching BETWEEN flex layouts (horizontal ↔ vertical)
		const isSwitchingBetweenFlexModes =
			(previousMode === LayoutMode.FlexHorizontal && layout.mode === LayoutMode.FlexVertical) ||
			(previousMode === LayoutMode.FlexVertical && layout.mode === LayoutMode.FlexHorizontal);


		if (childLayerIds && childLayerIds.length > 0) {
			if (isTransitioningToFlow || isSwitchingBetweenFlexModes || forceRedraw) {
				const ignoreFlowSet = new Set(layout.ignoreFlowIds ?? []);
				const fitContainerStyles = this.buildFitContainerStyles(layout.mode);

				childLayerIds.forEach(layerId => {
					// Skip ignore-flow children - they'll get absolute positioning below
					if (ignoreFlowSet.has(layerId)) {
						return;
					}

					const patch: LayerStyle = {};

					// Only reset positioning when transitioning from freeform
					if (isTransitioningToFlow || forceRedraw) {
						this.setStyleValue(patch, 'boxSizing', 'border-box');
						this.setStyleValue(patch, 'position', 'relative');
						this.setStyleValue(patch, 'top', 'unset');
						this.setStyleValue(patch, 'left', 'unset');
						this.setStyleValue(patch, 'right', 'unset');
						this.setStyleValue(patch, 'bottom', 'unset');
						this.setStyleValue(patch, 'transform', 'unset');
					}

					// Always apply fit container sizing when transitioning to flow or switching between flex modes unless elements are fixed size
					if (!fixedSizeChildren.includes(layerId)) {
						Object.entries(fitContainerStyles).forEach(([key, value]) => {
							this.setStyleValue(patch, key as keyof LayerStyle, value);
						});
					}

					patches[layerId] = patch;
				});
			}

			if (layout.alignment.stackingOrder) {
				let zIndex = 0;

				let layerIds = [];
				if (layout.alignment.stackingOrder === LayoutStackingOrder.FirstOnTop) {
					layerIds = [...childLayerIds.reverse()];
				} else {
					layerIds = [...childLayerIds];
				}

				for (const layerId of layerIds) {
					const patch: LayerStyle = patches[layerId] || {};

					this.setStyleValue(patch, 'zIndex', zIndex.toString());
					patches[layerId] = patch;

					zIndex++;
				}
			}
		}

		// Apply grid spans for grid layouts
		if (layout.mode === LayoutMode.Grid && layout.grid?.spans) {
			Object.entries(layout.grid.spans).forEach(([layerId, span]) => {
				if (!patches[layerId]) {
					patches[layerId] = {};
				}

				this.setStyleValue(
					patches[layerId],
					'gridColumn',
					this.buildSpanValue(span, 'columnStart', 'columnEnd')
				);
				this.setStyleValue(
					patches[layerId],
					'gridRow',
					this.buildSpanValue(span, 'rowStart', 'rowEnd')
				);
			});
		}

		// Apply position: absolute for ignore-flow children
		if (layout.ignoreFlowIds && layout.ignoreFlowIds.length > 0 && layout.mode !== LayoutMode.Freeform) {
			layout.ignoreFlowIds.forEach(layerId => {
				if (!patches[layerId]) {
					patches[layerId] = {};
				}
				this.setStyleValue(patches[layerId], 'position', 'absolute');
			});
		}

		const isTransitioningToFreeMode =
			previousMode !== LayoutMode.Freeform && layout.mode === LayoutMode.Freeform;

		if (isTransitioningToFreeMode && childLayerIds && childLayerIds.length > 0) {
			childLayerIds.forEach(layerId => {
				const ignoreFlowSet = new Set(layout.ignoreFlowIds ?? []);

				// Skip ignore-flow children - they'll get absolute positioning below
				if (ignoreFlowSet.has(layerId)) {
					return;
				}

				const patch: LayerStyle = {};
				this.setStyleValue(patch, 'position', 'absolute');
				this.setStyleValue(patch, 'top', '0');
				this.setStyleValue(patch, 'left', '0');
				this.setStyleValue(patch, 'right', 'auto');
				this.setStyleValue(patch, 'bottom', 'auto');
				this.setStyleValue(patch, 'transform', 'unset');
				//this.setStyleValue(patch, 'width', '100%');

				patches[layerId] = patch;
			})
		}

		return Object.keys(patches).length ? patches : undefined;
	}

	private static setStyleValue(
		target: LayerStyle,
		property: keyof LayerStyle,
		value: string | undefined
	): void {
		if (value === undefined) {
			(target as any)[property] = 'unset';
		} else {
			(target as any)[property] = value;
		}
	}

	private static toCssGap(value?: LayoutGapValue): string | undefined {
		if (value === null || value === undefined) {
			return undefined;
		}

		if (value === 'auto') {
			return 'auto';
		}

		if (typeof value === 'number') {
			return `${value}px`;
		}

		return value;
	}

	private static mapFlexMainAlignment(
		alignment?: LayoutMainAlignment
	): string | undefined {
		switch (alignment) {
			case LayoutMainAlignment.Center:
				return 'center';
			case LayoutMainAlignment.End:
				return 'flex-end';
			case LayoutMainAlignment.SpaceAround:
				return 'space-around';
			case LayoutMainAlignment.SpaceBetween:
				return 'space-between';
			case LayoutMainAlignment.SpaceEvenly:
				return 'space-evenly';
			case LayoutMainAlignment.Start:
			default:
				return 'flex-start';
		}
	}

	private static mapFlexCrossAlignment(
		alignment?: LayoutCrossAlignment,
		baseline?: boolean
	): string | undefined {
		console.log(baseline)
		console.log(alignment)
		if (baseline) {
			return 'baseline';
		}

		switch (alignment) {
			case LayoutCrossAlignment.Center:
				return 'center';
			case LayoutCrossAlignment.End:
				return 'flex-end';
			case LayoutCrossAlignment.Stretch:
				return 'stretch';
			case LayoutCrossAlignment.Baseline:
				return 'baseline';
			case LayoutCrossAlignment.Start:
			default:
				return 'flex-start';
		}
	}

	private static mapFlexAlignContent(
		alignment?: LayoutCrossAlignment,
		wrap?: string
	): string | undefined {
		if (wrap !== 'wrap') {
			return 'stretch';
		}

		switch (alignment) {
			case LayoutCrossAlignment.Center:
				return 'center';
			case LayoutCrossAlignment.End:
				return 'flex-end';
			case LayoutCrossAlignment.Stretch:
				return 'stretch';
			case LayoutCrossAlignment.Baseline:
				return 'baseline';
			case LayoutCrossAlignment.Start:
			default:
				return 'flex-start';
		}
	}

	private static mapGridMainAlignment(
		alignment?: LayoutMainAlignment
	): string | undefined {
		switch (alignment) {
			case LayoutMainAlignment.Center:
				return 'center';
			case LayoutMainAlignment.End:
				return 'end';
			case LayoutMainAlignment.SpaceAround:
				return 'space-around';
			case LayoutMainAlignment.SpaceBetween:
				return 'space-between';
			case LayoutMainAlignment.SpaceEvenly:
				return 'space-evenly';
			case LayoutMainAlignment.Start:
			default:
				return 'start';
		}
	}

	private static mapGridCrossAlignment(
		alignment?: LayoutCrossAlignment
	): string | undefined {
		switch (alignment) {
			case LayoutCrossAlignment.Center:
				return 'center';
			case LayoutCrossAlignment.End:
				return 'end';
			case LayoutCrossAlignment.Stretch:
				return 'stretch';
			case LayoutCrossAlignment.Baseline:
				return 'baseline';
			case LayoutCrossAlignment.Start:
			default:
				return 'start';
		}
	}

	private static mapGridItemAlignment(
		alignment?: LayoutCrossAlignment,
		baseline?: boolean
	): string | undefined {
		if (baseline) {
			return 'baseline';
		}

		return this.mapGridCrossAlignment(alignment);
	}

	private static buildTrackTemplate(tracks: LayoutTrackDefinition[]): string | undefined {
		if (!tracks.length) {
			return undefined;
		}

		const parts = tracks.map(track => this.getTrackValue(track)).filter(Boolean);
		return parts.length ? parts.join(' ') : undefined;
	}

	private static getTrackValue(track: LayoutTrackDefinition): string | undefined {
		switch (track.type) {
			case 'fr': {
				const fraction = track.value ?? '1';
				return `${fraction}fr`;
			}
			case 'fixed':
				return track.value ?? 'auto';
			case 'auto':
			default:
				return 'auto';
		}
	}

	private static buildSpanValue(
		span: LayoutGridChildSpan,
		startKey: keyof LayoutGridChildSpan,
		endKey: keyof LayoutGridChildSpan
	): string | undefined {
		const start = span[startKey];
		const end = span[endKey];

		if (start === undefined && end === undefined) {
			return undefined;
		}

		const startValue = start ?? 'auto';
		const endValue = end ?? 'auto';

		return `${startValue} / ${endValue}`;
	}
}
